import React, { useState, useEffect, useMemo, useContext } from 'react'
import { ConfigContext } from 'contexts/ConfigContext'
import { TextField } from '@material-ui/core'
import cloneDeep from 'clone-deep'
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap'
import objectPath from 'object-path'
import DatePickerCustom from '../../DatePicker/DatePickerCustom'
import moment from 'moment'
import FeatherIcon from 'feather-icons-react'
import '../score.css'
import { checkIsWarning } from '../Validation'
import TimePickerInput from '../../TimePicker/TimePickerInput'
import { useTimeFormat } from 'contexts/TimeFormatContext'
const TIME_FORMAT = 'HH:mm'

const inputStyles = {
  textAlign: 'center',
  fontSize: '14px',
}

const EditRowScore = ({
  uischema,
  score,
  scoreIndex,
  deleteScore,
  saveScore,
  warningChart,
  isCreateOnly,
  isReadOnly,
}) => {
  const today = moment().format('YYYY-MM-DD')
  const { formatTime } = useTimeFormat()
  const scoreActionDelete = objectPath.get(uischema, 'scoreAction.delete') || {}
  const fullConfig = objectPath.get(uischema, 'scoreConfig')
  const scoreSetting = objectPath.get(uischema, 'scoreSetting')
  const scoreConfig = fullConfig.filter(
    ({ isDate, isTime }) => !isDate && !isTime,
  )

  const configContext = useContext(ConfigContext)
  const { layoutType } = configContext.state

  const colorByTheme = useMemo(
    () => (layoutType === 'dark' ? '#adb7be' : '#495057'),
    [],
  )

  const scoreValueStyle = useMemo(() => {
    if (scoreSetting?.scoreWidth) {
      return {
        width: scoreSetting.scoreWidth,
      }
    }
    return {}
  }, [scoreSetting?.scoreWidth])

  const dateChart = useMemo(() => {
    return fullConfig.find(({ isDate, enabled }) => isDate && enabled) || {}
  }, [])

  const timeChart = useMemo(() => {
    return fullConfig.find(({ isTime, enabled }) => isTime && enabled) || {}
  }, [])

  const { isTime } = timeChart
  const { isDate } = dateChart
  const dateName = objectPath.get(dateChart, 'name') || 'date'
  const timeName = objectPath.get(timeChart, 'name') || 'time'
  const foundDate = objectPath.get(score, dateName)
  const foundTime = objectPath.get(score, timeName)

  const TooltipWarning = ({ scoreValue, isWarning, children }) => {
    let { name, ageRange, operator, value, icd10Code, disease, gender } =
      isWarning
    if (isWarning && scoreValue) {
      return (
        <OverlayTrigger
          key={name}
          placement="bottom"
          overlay={
            <Tooltip id={`tooltip-${name}`}>
              <ul className="pl-3 pr-0 py-0 my-0 text-left">
                <li className="p-0">
                  มีค่า {operator} {value}
                </li>
                {gender && <li>เพศ {gender}</li>}
                {ageRange && <li>ช่วงอายุ {ageRange}</li>}
                {icd10Code && (
                  <li>
                    โรค {icd10Code} : {disease}
                  </li>
                )}
              </ul>
            </Tooltip>
          }
        >
          {children}
        </OverlayTrigger>
      )
    }
    return children
  }

  const handleChangeTextField = (name, value) => {
    objectPath.set(score, name, value)
    setRender(true)
    saveScore(scoreIndex, score, isCreateOnly)
  }

  const updateDate = (date) => {
    handleChangeTextField(dateName, date)
  }

  const updateTime = (time) => {
    handleChangeTextField(timeName, time)
  }

  const updateToday = () => {
    let today = moment().format('YYYY-MM-DD HH:mm:ss')
    let time = formatTime(today, TIME_FORMAT)
    updateTime(time)
  }

  const [render, setRender] = useState(false)

  useEffect(() => {
    if (render) {
      setRender(false)
    }
  }, [render])

  useEffect(() => {
    if (isTime && !foundTime) {
      updateToday()
    }
  }, [foundTime])

  return (
    <div>
      <div>
        {isDate && (
          <div className="body-box d-flex">
            <div className="d-flex border w-100 p-2">
              {isReadOnly ? (
                <span className="align-content-center">{foundDate}</span>
              ) : (
                <DatePickerCustom
                  value={foundDate}
                  onChange={(date) => updateDate(date.value)}
                  formatDisplay={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                  placeholder={dateChart?.date?.format ?? 'DD/MM/YYYY'}
                  isClearable={true}
                  maskSign={dateChart?.date?.maskSign ?? '/'}
                  calendar={dateChart?.date?.calendar}
                />
              )}
            </div>
          </div>
        )}
        {isTime && (
          <div className="body-box d-flex">
            <div className="d-flex border w-100 p-2">
              {isReadOnly ? (
                <span className="align-content-center">{foundTime}</span>
              ) : (
                <TimePickerInput
                  value={foundTime}
                  onChange={(e) => {
                    let date = `${today} ${e}`
                    let time = formatTime(date, TIME_FORMAT)
                    updateTime(time)
                  }}
                  formatDisplay={TIME_FORMAT}
                  placeholder={TIME_FORMAT}
                  isClearable={true}
                />
              )}
            </div>
          </div>
        )}
        <>
          {scoreConfig.map((config) => {
            let { group, name, data, enabled } = config
            let scoreValue = objectPath.get(score, name)
            let isWarning = checkIsWarning(warningChart, name, scoreValue)
            if (!enabled) return
            if (group) {
              return (
                <div>
                  {data.map(({ name, enabled }) => {
                    if (!enabled) return
                    let scoreValue = objectPath.get(score, name)

                    if (isReadOnly) {
                      return (
                        <TooltipWarning
                          scoreValue={scoreValue}
                          isWarning={isWarning}
                        >
                          <div
                            className={`sub-box ${
                              isWarning ? 'text-danger' : ''
                            }`}
                            style={{ ...scoreValueStyle, color: colorByTheme }}
                          >
                            {scoreValue}
                          </div>
                        </TooltipWarning>
                      )
                    }

                    return (
                      <div className="sub-box" style={scoreValueStyle}>
                        <TextField
                          inputProps={{
                            style: {
                              ...inputStyles,
                              color: isWarning ? 'red' : colorByTheme,
                            },
                          }}
                          autoComplete="off"
                          value={scoreValue}
                          onChange={(e) =>
                            handleChangeTextField(name, e.target.value)
                          }
                        />
                      </div>
                    )
                  })}
                </div>
              )
            }

            if (isReadOnly) {
              return (
                <TooltipWarning scoreValue={scoreValue} isWarning={isWarning}>
                  <div
                    className={`sub-box ${isWarning ? 'text-danger' : ''}`}
                    style={{ ...scoreValueStyle, color: colorByTheme }}
                  >
                    {scoreValue}
                  </div>
                </TooltipWarning>
              )
            }

            return (
              <div className="sub-box" style={scoreValueStyle}>
                <TextField
                  inputProps={{
                    style: {
                      ...inputStyles,
                      color: isWarning ? 'red' : colorByTheme,
                    },
                  }}
                  autoComplete="off"
                  value={scoreValue}
                  onChange={(e) => handleChangeTextField(name, e.target.value)}
                />
              </div>
            )
          })}
        </>
      </div>
      {!isReadOnly && (
        <div className="d-flex align-items-end justify-content-end">
          <div className="d-flex">
            {scoreActionDelete?.isEnabled && (
              <div className="mx-1">
                <Button
                  className="score-button"
                  variant="outline-danger"
                  onClick={() => deleteScore(scoreIndex)}
                >
                  <FeatherIcon icon="trash" size="14" />
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
export default EditRowScore
